import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { NAV_LINKS } from "../data/content";
import { scrollToTarget } from "./ui-bits";

const ALT_MIN = 3800;
const ALT_MAX = 6120;

function CompassDial() {
  const needle = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (!needle.current) return;
      const deg = (e.clientX / window.innerWidth) * 360 - 180;
      needle.current.style.transform = `rotate(${deg}deg)`;
    };
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, []);
  return (
    <svg viewBox="0 0 40 40" className="h-9 w-9 shrink-0" aria-hidden="true" data-testid="nav-compass">
      <circle cx="20" cy="20" r="18.5" fill="none" stroke="rgba(255,255,255,0.25)" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
        <line key={a} x1="20" y1="3" x2="20" y2="7.5" stroke="rgba(255,255,255,0.4)" strokeWidth="1" transform={`rotate(${a} 20 20)`} />
      ))}
      <g ref={needle} style={{ transformOrigin: "20px 20px", transition: "transform 0.25s ease-out" }}>
        <polygon points="20,7 23,20 20,33 17,20" fill="#FF6B35" fillOpacity="0.9" />
      </g>
      <circle cx="20" cy="20" r="1.8" fill="#F8FAFC" />
    </svg>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [alt, setAlt] = useState(ALT_MIN);
  const { scrollYProgress } = useScroll();
  useMotionValueEvent(scrollYProgress, "change", (v) =>
    setAlt(Math.round(ALT_MIN + v * (ALT_MAX - ALT_MIN)))
  );

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToTarget(href);
  };

  return (
    <>
      <header className="glass fixed inset-x-0 top-0 z-50 border-b border-white/10">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-6 px-5 md:px-8">
          <a href="#hero" data-testid="nav-brand" onClick={(e) => go(e, "#hero")} className="flex items-center gap-3">
            <CompassDial />
            <span className="whitespace-nowrap leading-tight">
              <span className="block whitespace-nowrap font-display text-lg font-black tracking-wide text-white">AMIT RAWAT</span>
              <span className="block whitespace-nowrap font-mono text-[8px] tracking-[0.3em] text-cream/50">EXPEDITION PORTFOLIO</span>
            </span>
          </a>

          <nav className="hidden items-center gap-6 lg:flex">
            {NAV_LINKS.slice(1, 7).map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                data-testid={`nav-${l.id}-link`}
                onClick={(e) => go(e, `#${l.id}`)}
                className="whitespace-nowrap font-mono text-[10px] tracking-[0.22em] text-slate-400 transition-colors hover:text-ember"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-4 md:gap-6">
            <div className="hidden items-center gap-3 whitespace-nowrap font-mono text-[10px] tracking-[0.2em] text-slate-400 sm:flex" data-testid="nav-altitude-hud">
              <span className="relative h-[3px] w-14 overflow-hidden bg-white/10">
                <motion.span className="absolute left-0 top-0 h-full w-full origin-left bg-ember" style={{ scaleX: scrollYProgress }} />
              </span>
              <span className="text-ember">ALT {alt.toLocaleString("en-US")} M</span>
            </div>
            <a
              href="#contact"
              onClick={(e) => go(e, "#contact")}
              data-testid="nav-cta"
              className="btn-primary hidden items-center gap-2 whitespace-nowrap px-4 py-2 font-mono text-[10px] font-bold tracking-[0.2em] md:flex"
            >
              START AN EXPEDITION <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
            <button className="lg:hidden" data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} aria-label="Toggle menu">
              {open ? <X className="h-6 w-6 text-white" /> : <Menu className="h-6 w-6 text-white" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="glass fixed inset-x-0 top-[72px] z-40 border-b border-white/10 lg:hidden"
            data-testid="nav-mobile-drawer"
          >
            <nav className="flex flex-col px-6 py-4">
              {NAV_LINKS.map((l, i) => (
                <a
                  key={l.id}
                  href={`#${l.id}`}
                  onClick={(e) => go(e, `#${l.id}`)}
                  data-testid={`nav-mobile-${l.id}-link`}
                  className="flex items-baseline justify-between border-b border-white/5 py-3.5 font-display text-2xl font-bold uppercase tracking-wide text-white transition-colors last:border-0 hover:text-ember"
                >
                  {l.label}
                  <span className="font-mono text-[10px] text-slate-500">0{i + 1}</span>
                </a>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
